import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator, TrendingUp, Share2, FileSpreadsheet, MessageCircle, Percent, Edit2 } from "lucide-react";
import { toast } from "sonner";
import { lenders } from "@/data/payoutsData";
import { LOAN_TYPE_LABELS, LoanType } from "@/types";

const loanTypes: LoanType[] = ['home_loan', 'lap', 'personal_loan', 'business_loan', 'car_loan'];

const EMI_NOTES_KEY = 'emi_calculator_notes';

interface EMINotes {
  shortNote: string;
  detailedNote: string;
}

const getStoredNotes = (): EMINotes => {
  const stored = localStorage.getItem(EMI_NOTES_KEY);
  return stored ? JSON.parse(stored) : { shortNote: '', detailedNote: '' };
};

const saveNotes = (notes: EMINotes) => {
  localStorage.setItem(EMI_NOTES_KEY, JSON.stringify(notes));
};

export function EMICalculator() {
  const [activeTab, setActiveTab] = useState<'emi' | 'foir'>('emi');
  const [shareTab, setShareTab] = useState<'short' | 'detailed'>('short');
  const [isEditingShort, setIsEditingShort] = useState(false);
  const [isEditingDetailed, setIsEditingDetailed] = useState(false);
  const [editedShortNote, setEditedShortNote] = useState('');
  const [editedDetailedNote, setEditedDetailedNote] = useState('');
  const [isShortResetSaved, setIsShortResetSaved] = useState(false);
  const [isDetailedResetSaved, setIsDetailedResetSaved] = useState(false);
  
  // EMI Calculator state
  const [loanAmount, setLoanAmount] = useState(5000000);
  const [interestRate, setInterestRate] = useState(8.5);
  const [tenure, setTenure] = useState(20);
  
  // FOIR Calculator state
  const [monthlyIncome, setMonthlyIncome] = useState(100000);
  const [existingEMIs, setExistingEMIs] = useState(0);
  const [foirLimit, setFoirLimit] = useState(50); // 50% default FOIR limit
  
  // Excel Generation state
  const [selectedLender, setSelectedLender] = useState("");
  const [selectedLoanType, setSelectedLoanType] = useState<LoanType>("home_loan");

  const calculateEMI = () => {
    const principal = loanAmount;
    const monthlyRate = interestRate / 12 / 100;
    const months = tenure * 12;
    
    const emi = (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) / 
                (Math.pow(1 + monthlyRate, months) - 1);
    
    const totalPayment = emi * months;
    const totalInterest = totalPayment - principal;
    
    return {
      emi: Math.round(emi),
      totalPayment: Math.round(totalPayment),
      totalInterest: Math.round(totalInterest),
    };
  };

  const calculateFOIR = () => {
    const maxEMI = (monthlyIncome * foirLimit / 100) - existingEMIs;
    const availableForEMI = Math.max(0, maxEMI);
    
    // Calculate max loan amount based on available EMI
    const monthlyRate = interestRate / 12 / 100;
    const months = tenure * 12;
    
    let maxLoanAmount = 0;
    if (availableForEMI > 0 && monthlyRate > 0) {
      maxLoanAmount = (availableForEMI * (Math.pow(1 + monthlyRate, months) - 1)) / 
                      (monthlyRate * Math.pow(1 + monthlyRate, months));
    }
    
    const currentFOIR = monthlyIncome > 0 
      ? ((existingEMIs + calculateEMI().emi) / monthlyIncome * 100) 
      : 0;
    
    return {
      maxEMI: Math.round(availableForEMI),
      maxLoanAmount: Math.round(maxLoanAmount),
      currentFOIR: currentFOIR.toFixed(1),
      isEligible: currentFOIR <= foirLimit,
    };
  };

  const result = calculateEMI();
  const foirResult = calculateFOIR();

  const formatCurrency = (amount: number) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const generateShortNote = () => {
    return `EMI Details:
• Loan Amount: ${formatCurrency(loanAmount)}
• Interest Rate: ${interestRate}% p.a.
• Tenure: ${tenure} years
• Monthly EMI: ${formatCurrency(result.emi)}
• Total Interest: ${formatCurrency(result.totalInterest)}
• Total Payment: ${formatCurrency(result.totalPayment)}`;
  };

  const generateDetailedNote = () => {
    return `📊 LOAN ELIGIBILITY SUMMARY
━━━━━━━━━━━━━━━━━━━━━━
🏦 LOAN DETAILS:
• Principal Amount: ${formatCurrency(loanAmount)}
• Interest Rate: ${interestRate}% per annum
• Loan Tenure: ${tenure} years (${tenure * 12} months)

💰 EMI CALCULATION:
• Monthly EMI: ${formatCurrency(result.emi)}
• Total Interest Payable: ${formatCurrency(result.totalInterest)}
• Total Amount Payable: ${formatCurrency(result.totalPayment)}

📈 FOIR ANALYSIS:
• Monthly Income: ${formatCurrency(monthlyIncome)}
• Existing EMIs: ${formatCurrency(existingEMIs)}
• FOIR Limit: ${foirLimit}%
• Current FOIR: ${foirResult.currentFOIR}%
• Max Eligible EMI: ${formatCurrency(foirResult.maxEMI)}
• Max Eligible Loan: ${formatCurrency(foirResult.maxLoanAmount)}
• Eligibility Status: ${foirResult.isEligible ? '✅ ELIGIBLE' : '❌ NOT ELIGIBLE'}

━━━━━━━━━━━━━━━━━━━━━━
Contact for best loan rates!`;
  };

  // Initialize editable notes on mount and when values change
  useEffect(() => {
    const storedNotes = getStoredNotes();
    if (storedNotes.shortNote) {
      setEditedShortNote(storedNotes.shortNote);
    } else {
      setEditedShortNote(generateShortNote());
    }
    if (storedNotes.detailedNote) {
      setEditedDetailedNote(storedNotes.detailedNote);
    } else {
      setEditedDetailedNote(generateDetailedNote());
    }
  }, []);

  // Update generated notes when values change (always regenerate for live updates)
  useEffect(() => {
  if (isShortResetSaved) {
    setEditedShortNote(generateShortNote());
  }
  if (isDetailedResetSaved) {
    setEditedDetailedNote(generateDetailedNote());
  }
}, [loanAmount, interestRate, tenure, monthlyIncome, existingEMIs, foirLimit, isShortResetSaved, isDetailedResetSaved]);
const handleSaveShortNote = () => {
  const notes = getStoredNotes();
  notes.shortNote = editedShortNote;
  saveNotes(notes);
  setIsShortResetSaved(true);
  setIsEditingShort(false);
  toast.success("Short note saved");
};
  const handleSaveDetailedNote = () => {
  const notes = getStoredNotes();
  notes.detailedNote = editedDetailedNote;
  saveNotes(notes);
  setIsDetailedResetSaved(true);
  setIsEditingDetailed(false);
  toast.success("Detailed note saved");
};
const handleResetShortNote = () => {
  setEditedShortNote(generateShortNote());
  setIsShortResetSaved(false);
  const notes = getStoredNotes();
  notes.shortNote = '';
  saveNotes(notes);
  toast.success("Short note reset to default");
};

const handleResetDetailedNote = () => {
  setEditedDetailedNote(generateDetailedNote());
  setIsDetailedResetSaved(false);
  const notes = getStoredNotes();
  notes.detailedNote = '';
  saveNotes(notes);
  toast.success("Detailed note reset to default");
};

  const handleShareWhatsApp = (content: string) => {
    const url = `https://wa.me/?text=${encodeURIComponent(content)}`;
    window.open(url, '_blank');
    toast.success("Opening WhatsApp to share");
  };

  const generateExcel = () => {
    if (!selectedLender) {
      toast.error("Please select a lender");
      return;
    }

    // Generate CSV content that can be opened in Excel
    const csvContent = `Loan EMI Plan - ${selectedLender}
,
Loan Type,${LOAN_TYPE_LABELS[selectedLoanType]}
Lender,${selectedLender}
,
LOAN DETAILS,
Principal Amount,${loanAmount}
Interest Rate (p.a.),${interestRate}%
Loan Tenure,${tenure} years
Total Months,${tenure * 12}
,
EMI CALCULATION,
Monthly EMI,${result.emi}
Total Interest Payable,${result.totalInterest}
Total Amount Payable,${result.totalPayment}
,
FOIR ANALYSIS,
Monthly Income,${monthlyIncome}
Existing EMIs,${existingEMIs}
FOIR Limit,${foirLimit}%
Current FOIR,${foirResult.currentFOIR}%
Max Eligible EMI,${foirResult.maxEMI}
Max Eligible Loan Amount,${foirResult.maxLoanAmount}
Eligibility Status,${foirResult.isEligible ? 'ELIGIBLE' : 'NOT ELIGIBLE'}
,
AMORTIZATION SCHEDULE (Complete ${tenure * 12} Months Breakdown),
Month,Opening Balance,EMI,Principal,Interest,Closing Balance`;

    // Generate complete amortization for all months
    let balance = loanAmount;
    const monthlyRate = interestRate / 12 / 100;
    let amortization = '';
    
    for (let month = 1; month <= tenure * 12; month++) {
      const interest = balance * monthlyRate;
      const principal = result.emi - interest;
      const closingBalance = balance - principal;
      amortization += `\n${month},${Math.round(balance)},${result.emi},${Math.round(principal)},${Math.round(interest)},${Math.round(closingBalance)}`;
      balance = closingBalance;
    }

    const finalCsv = csvContent + amortization;
    
    // Create and download file
    const blob = new Blob([finalCsv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const fileName = `EMI_Plan_${selectedLender.replace(/\s/g, '_')}_${selectedLoanType}_${new Date().toISOString().split('T')[0]}.csv`;
    
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    
    toast.success("Excel file generated successfully!");
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'emi' | 'foir')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="emi" className="flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            EMI Calculator
          </TabsTrigger>
          <TabsTrigger value="foir" className="flex items-center gap-2">
            <Percent className="h-4 w-4" />
            FOIR Eligibility
          </TabsTrigger>
        </TabsList>

        <TabsContent value="emi" className="space-y-4 mt-4">
          <Card variant="elevated">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Calculator className="h-5 w-5 text-accent" />
                Loan Parameters
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Loan Amount */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label>Loan Amount</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={loanAmount || ''}
                      onChange={(e) => setLoanAmount(Number(e.target.value) || 0)}
                      onBlur={(e) => {
                        const val = Number(e.target.value);
                        if (val < 100000) {
                          setLoanAmount(100000);
                          toast.error("Minimum loan amount is ₹1L");
                        } else if (val > 100000000) {
                          setLoanAmount(100000000);
                          toast.error("Maximum loan amount is ₹10Cr");
                        }
                      }}
                      className="w-32 text-right text-sm h-8"
                    />
                    <span className="text-sm text-accent font-medium w-16 text-right">
                      {formatCurrency(loanAmount)}
                    </span>
                  </div>
                </div>
                <Slider
                  value={[loanAmount]}
                  onValueChange={(v) => setLoanAmount(v[0])}
                  min={100000}
                  max={100000000}
                  step={100000}
                  className="[&_[role=slider]]:bg-accent"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>₹1L</span>
                  <span>₹10Cr</span>
                </div>
              </div>

              {/* Interest Rate */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label>Interest Rate (p.a.)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.1"
                      value={interestRate || ''}
                      onChange={(e) => setInterestRate(Number(e.target.value) || 0)}
                      onBlur={(e) => {
                        const val = Number(e.target.value);
                        if (val < 1) {
                          setInterestRate(1);
                          toast.error("Minimum interest rate is 1%");
                        } else if (val > 30) {
                          setInterestRate(30);
                          toast.error("Maximum interest rate is 30%");
                        }
                      }}
                      className="w-20 text-right text-sm h-8"
                    />
                    <span className="text-sm text-accent font-medium">{interestRate}%</span>
                  </div>
                </div>
                <Slider
                  value={[interestRate]}
                  onValueChange={(v) => setInterestRate(v[0])}
                  min={1}
                  max={30}
                  step={0.1}
                  className="[&_[role=slider]]:bg-accent"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>1%</span>
                  <span>30%</span>
                </div>
              </div>

              {/* Tenure */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label>Loan Tenure</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={tenure || ''}
                      onChange={(e) => setTenure(Number(e.target.value) || 0)}
                      onBlur={(e) => {
                        const val = Number(e.target.value);
                        if (val < 1) {
                          setTenure(1);
                          toast.error("Minimum tenure is 1 year");
                        } else if (val > 30) {
                          setTenure(30);
                          toast.error("Maximum tenure is 30 years");
                        }
                      }}
                      className="w-20 text-right text-sm h-8"
                    />
                    <span className="text-sm text-accent font-medium">{tenure} years</span>
                  </div>
                </div>
                <Slider
                  value={[tenure]}
                  onValueChange={(v) => setTenure(v[0])}
                  min={1}
                  max={30}
                  step={1}
                  className="[&_[role=slider]]:bg-accent"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>1 year</span>
                  <span>30 years</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results */}
          <Card variant="accent">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Monthly EMI</span>
                <span className="text-3xl font-bold text-accent">
                  {formatCurrency(result.emi)}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-accent/20">
                <div>
                  <p className="text-sm text-muted-foreground">Total Interest</p>
                  <p className="text-lg font-semibold">{formatCurrency(result.totalInterest)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Payment</p>
                  <p className="text-lg font-semibold">{formatCurrency(result.totalPayment)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="foir" className="space-y-4 mt-4">
          <Card variant="elevated">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Percent className="h-5 w-5 text-accent" />
                FOIR Parameters
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Monthly Income */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label>Monthly Income</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={monthlyIncome || ''}
                      onChange={(e) => setMonthlyIncome(Number(e.target.value) || 0)}
                      onBlur={(e) => {
                        const val = Number(e.target.value);
                        if (val < 10000) {
                          setMonthlyIncome(10000);
                          toast.error("Minimum monthly income is ₹10,000");
                        }
                      }}
                      className="w-32 text-right text-sm h-8"
                    />
                    <span className="text-sm text-accent font-medium w-16 text-right">
                      {formatCurrency(monthlyIncome)}
                    </span>
                  </div>
                </div>
                <Slider
                  value={[monthlyIncome]}
                  onValueChange={(v) => setMonthlyIncome(v[0])}
                  min={10000}
                  max={1000000}
                  step={5000}
                  className="[&_[role=slider]]:bg-accent"
                />
              </div>

              {/* Existing EMIs */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label>Existing EMIs</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={existingEMIs || ''}
                      onChange={(e) => setExistingEMIs(Number(e.target.value) || 0)}
                      onBlur={(e) => {
                        const val = Number(e.target.value);
                        if (val < 0) {
                          setExistingEMIs(0);
                          toast.error("Existing EMIs cannot be negative");
                        }
                      }}
                      className="w-32 text-right text-sm h-8"
                    />
                    <span className="text-sm text-accent font-medium w-16 text-right">
                      {formatCurrency(existingEMIs)}
                    </span>
                  </div>
                </div>
                <Slider
                  value={[existingEMIs]}
                  onValueChange={(v) => setExistingEMIs(v[0])}
                  min={0}
                  max={monthlyIncome * 0.8}
                  step={1000}
                  className="[&_[role=slider]]:bg-accent"
                />
              </div>

              {/* FOIR Limit */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label>FOIR Limit</Label>
                  <span className="text-sm text-accent font-medium">{foirLimit}%</span>
                </div>
                <Slider
                  value={[foirLimit]}
                  onValueChange={(v) => setFoirLimit(v[0])}
                  min={30}
                  max={80}
                  step={5}
                  className="[&_[role=slider]]:bg-accent"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>30%</span>
                  <span>80%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* FOIR Results */}
          <Card className={foirResult.isEligible ? "bg-success/10 border-success/30" : "bg-destructive/10 border-destructive/30"}>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Current FOIR</span>
                <span className={`text-3xl font-bold ${foirResult.isEligible ? 'text-success' : 'text-destructive'}`}>
                  {foirResult.currentFOIR}%
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border/50">
                <div>
                  <p className="text-sm text-muted-foreground">Max Eligible EMI</p>
                  <p className="text-lg font-semibold">{formatCurrency(foirResult.maxEMI)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Max Eligible Loan</p>
                  <p className="text-lg font-semibold">{formatCurrency(foirResult.maxLoanAmount)}</p>
                </div>
              </div>
              <div className={`text-center py-2 rounded-lg ${foirResult.isEligible ? 'bg-success/20 text-success' : 'bg-destructive/20 text-destructive'}`}>
                {foirResult.isEligible ? '✅ Customer is Eligible' : '❌ Customer Not Eligible (FOIR Exceeded)'}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Sharing & Export Section */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Share2 className="h-5 w-5 text-muted-foreground" />
            Share & Export EMI Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Share Tabs */}
          <Tabs value={shareTab} onValueChange={(v) => setShareTab(v as 'short' | 'detailed')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="short">Short</TabsTrigger>
              <TabsTrigger value="detailed">Detailed</TabsTrigger>
            </TabsList>

            <TabsContent value="short" className="space-y-3 mt-3">
              {isEditingShort ? (
                <Textarea
                  value={editedShortNote}
                  onChange={(e) => setEditedShortNote(e.target.value)}
                  rows={8}
                  className="text-sm rounded-xl"
                />
              ) : (
                <pre className="text-sm whitespace-pre-wrap bg-muted/50 p-3 rounded-xl border">{editedShortNote}</pre>
              )}
              <div className="flex gap-2">
                {isEditingShort ? (
                  <>
                    <Button size="sm" onClick={handleSaveShortNote} className="flex-1 rounded-lg">Save</Button>
                    <Button size="sm" variant="outline" onClick={() => setIsEditingShort(false)} className="rounded-lg">Cancel</Button>
                    <Button size="sm" variant="ghost" onClick={handleResetShortNote} className="rounded-lg">Reset</Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setIsEditingShort(true)} className="rounded-lg">
                      <Edit2 className="h-4 w-4 mr-1" />Edit
                    </Button>
                    <Button size="sm" variant="success" onClick={() => handleShareWhatsApp(editedShortNote)} className="flex-1 rounded-lg">
                      <MessageCircle className="h-4 w-4 mr-1" />Share
                    </Button>
                  </>
                )}
              </div>
            </TabsContent>

            <TabsContent value="detailed" className="space-y-3 mt-3">
              {isEditingDetailed ? (
                <Textarea
                  value={editedDetailedNote}
                  onChange={(e) => setEditedDetailedNote(e.target.value)}
                  rows={12}
                  className="text-sm rounded-xl"
                />
              ) : (
                <pre className="text-sm whitespace-pre-wrap bg-muted/50 p-3 rounded-xl border max-h-64 overflow-y-auto">{editedDetailedNote}</pre>
              )}
              <div className="flex gap-2">
                {isEditingDetailed ? (
                  <>
                    <Button size="sm" onClick={handleSaveDetailedNote} className="flex-1 rounded-lg">Save</Button>
                    <Button size="sm" variant="outline" onClick={() => setIsEditingDetailed(false)} className="rounded-lg">Cancel</Button>
                    <Button size="sm" variant="ghost" onClick={handleResetDetailedNote} className="rounded-lg">Reset</Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setIsEditingDetailed(true)} className="rounded-lg">
                      <Edit2 className="h-4 w-4 mr-1" />Edit
                    </Button>
                    <Button size="sm" variant="success" onClick={() => handleShareWhatsApp(editedDetailedNote)} className="flex-1 rounded-lg">
                      <MessageCircle className="h-4 w-4 mr-1" />Share
                    </Button>
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>

          {/* Excel Generation */}
          <div className="pt-3 border-t space-y-3">
            <p className="text-sm text-muted-foreground">Generate Excel with Lender Branding</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Lender</Label>
                <Select value={selectedLender} onValueChange={setSelectedLender}>
                  <SelectTrigger className="h-9 rounded-lg">
                    <SelectValue placeholder="Select Lender" />
                  </SelectTrigger>
                  <SelectContent>
                    {lenders.map(lender => (
                      <SelectItem key={lender} value={lender}>{lender}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Product</Label>
                <Select value={selectedLoanType} onValueChange={(v) => setSelectedLoanType(v as LoanType)}>
                  <SelectTrigger className="h-9 rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {loanTypes.map(type => (
                      <SelectItem key={type} value={type}>{LOAN_TYPE_LABELS[type]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button 
              variant="secondary" 
              className="w-full rounded-xl"
              onClick={generateExcel}
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Generate Excel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
